import {Injectable} from '@angular/core';
import {Location} from '@angular/common';
import {NavigationEnd, NavigationStart, Router} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {filter} from 'rxjs/operators';

const isHome = (url: string): boolean => url.split(/[?#]/)[0] === '/home';

// Navigation around home: the back arrow for the pages the home menu opens, and the pop
// kiosk sign-out does first. When the menu pushed the page on top of
// home, the arrow pops back to it, which is the same entry system back lands on. Anywhere
// else (a deep link, a reload, the kiosk flows that replaceUrl to /switch-user) there is
// no home entry underneath to pop to, and location.back() would leave the app or go to
// the previous site, so the arrow navigates home instead.
//
// Root-provided but only useful if something has injected it before the first
// navigation, so AppComponent does.
@Injectable({providedIn: 'root'})
export class NavService {
  private previousUrl = '';
  private pushedFromHome = false;
  private pending = false;

  constructor(
    private readonly router: Router,
    private readonly location: Location,
  ) {
    this.router.events.pipe(
      filter((e): e is NavigationStart => e instanceof NavigationStart),
    ).subscribe(() => {
      // extras are only readable while the navigation is in flight, so they are read at
      // the start and applied once it ends. A popstate (system back, or this service's
      // own location.back()) is never a push.
      const nav = this.router.getCurrentNavigation();
      this.pending = nav?.trigger === 'imperative' && !nav.extras.replaceUrl;
    });

    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    ).subscribe((e) => {
      const url = e.urlAfterRedirects;
      this.pushedFromHome = this.pending && isHome(this.previousUrl) && !isHome(url);
      this.previousUrl = url;
    });
  }

  // Resolves once the navigation has landed, for callers that show something on home.
  async back(): Promise<void> {
    if (this.pushedFromHome) {
      const ended = firstValueFrom(this.router.events.pipe(filter((e) => e instanceof NavigationEnd)));
      this.location.back();
      await ended;
    } else {
      await this.router.navigate(['/home'], {replaceUrl: true});
    }
  }

  // For a flow about to replace the current page (kiosk sign-out). Replacing a page the
  // menu pushed would leave home underneath it, and a later replace of that with home
  // makes two adjacent home entries; popping first makes the replace land on home's own
  // entry, so the stack is the same wherever the user was.
  async popToHome(): Promise<void> {
    if (this.pushedFromHome) {
      await this.back();
    }
  }
}
